import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, Subject, switchMap, take, throwError } from 'rxjs';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { AuthDataService } from '../../data-access/data/auth-data.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const router = inject(Router);
  const modal = inject(NgbModal);
  const authDataService = inject(AuthDataService);

  let isRefreshing = false;
  let isNavigatingToError = false;
  const refreshSubject = new Subject<string>();

  const getAccessToken = () => sessionStorage.getItem('puAccessToken');
  const getRefreshToken = () => sessionStorage.getItem('puRefreshToken');

  const clearSession = () => {
    sessionStorage.removeItem('puAccessToken');
    sessionStorage.removeItem('puRefreshToken');
    modal.dismissAll();
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let message = 'An unexpected error occurred. Please try again.';
      const errorMsg = error.error?.message || error.error?.errors?.[0]?.message;

      // Network / client errors
      if (error.error instanceof ErrorEvent) {
        toast.show(`Network error: ${error.error.message}`, 'error');
        return throwError(() => error);
      }

      if (error.status === 0) {
        toast.show('Unable to connect to the server. Please check your connection.', 'error');
        return throwError(() => error);
      }

      /**
       * 401 — Try refresh token first
       */
      if (error.status === 401 && !req.url.includes('/token') && getRefreshToken()) {
        if (!isRefreshing) {
          isRefreshing = true;

          return authDataService.refreshToken().pipe(
            switchMap(() => {
              isRefreshing = false;

              const newAccessToken = getAccessToken();
              if (!newAccessToken) {
                throw error;
              }

              refreshSubject.next(newAccessToken);

              // Retry original request
              return next(
                req.clone({
                  headers: req.headers.set('Authorization', `Bearer ${newAccessToken}`)
                })
              );
            }),
            catchError(() => {
              isRefreshing = false;
              clearSession();

              if (!isNavigatingToError) {
                isNavigatingToError = true;
                router.navigate(['/error'], {
                  queryParams: { code: 401 },
                  state: { fromApp: true }
                });
              }

              return throwError(() => error);
            })
          );
        }

        // Wait for refresh to finish
        return refreshSubject.pipe(
          take(1),
          switchMap(token =>
            next(
              req.clone({
                headers: req.headers.set('Authorization', `Bearer ${token}`)
              })
            )
          )
        );
      }

      /**
       * Other backend errors
       */
      switch (error.status) {
        case 400:
          message = errorMsg || 'Bad request.';
          break;

        case 403:
          message = errorMsg || 'You do not have permission to perform this action.';
          break;

        case 404:
          message = errorMsg || 'The requested resource was not found.';
          break;

        case 410:
          message = errorMsg || 'The link has expired.';
          clearSession();

          if (!isNavigatingToError) {
            isNavigatingToError = true;
            router.navigate(['/error'], {
              queryParams: { code: 410 }
            });
          }
          break;

        case 422:
          message =
            errorMsg || 'Some of the provided data is invalid. Please review and try again.';
          break;

        case 500:
          message = errorMsg || 'A server error occurred. Please try again later.';
          break;

        case 503:
          message = errorMsg || 'Unable to process request.';
          clearSession();

          if (!isNavigatingToError) {
            isNavigatingToError = true;
            router.navigate(['/error'], {
              queryParams: { code: 503 }
            });
          }

          break;

        default:
          message = errorMsg || `Error ${error.status}: ${error.statusText}`;
      }

      toast.show(message, 'error');
      return throwError(() => error);
    })
  );
};
